import React, { useState, useEffect,useRef  } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  Divider,
  InputAdornment, Modal, Typography, Button, Skeleton,
} from "@mui/material";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { pdfToolApi } from "../../services/PdfEditor/pdfToolApi";
import { esignIcons } from "../Esign/icons";
import Loader from "../Loader";
import { fetchPdfEditorDetails, refreshToken } from "../../store/reducers/pdfEditorTokenSlice";
import { apiEndpoint } from "../../urls";
import PdfPreview from "../PdfPreview/pdfPreview";
import { useLogger } from "../../hooks/useLogger";

const DraftDocuments = () => {
  const logEvent = useLogger();
  const fetchCalled = useRef(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [documents, setDocuments] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loader, setLoader] = useState(false);
  const [tableloader, setTableLoader] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(false);
  const [previewDoc, setPreviewDoc] = useState({});
  const [deleteDraftPdfOpen, setDeleteDraftPdfOpen] = useState(false);
  const [draftDoc, setDraftDoc] = useState({});
  const { open } = useOutletContext();
  const { pdfEditortoken } = useSelector((state) => state.pdfEditortoken);
  const { profileData } = useSelector((state) => state.profile);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedPreviewDoc, setSelectedPreviewDoc] = useState(null);

  const fetchAllDocuments = async (AccessToken) => {
    if (!profileData?.username) {
      console.error("Profile username is missing.");
      return;
    }  
    setTableLoader(true);
    try {
      const newTokenData = await dispatch(fetchPdfEditorDetails(profileData.username)).unwrap();      
      const accessToken = newTokenData?.access || AccessToken;
      if (!accessToken) {
        throw new Error("No access token available.");
      }
  
      const response = await pdfToolApi.getAllDocuments(accessToken);
      if (response) {
        setDocuments(response.data);
      }
    } catch (error) {
      console.error("Fetching documents failed:", error);
    } finally {
      setTableLoader(false);
    }
  };
  

  useEffect(() => {
    if (!fetchCalled.current && pdfEditortoken?.access) {
      fetchCalled.current = true;
      fetchAllDocuments(pdfEditortoken?.access);
    }
  }, [pdfEditortoken?.access]);

  useEffect(() => {
    if (selectedDoc) {
      handleNewRequest(selectedDoc);
    }
  }, [selectedDoc]);

  const handleNewRequest = async (selectedDoc) => {
    logEvent('Clicked', 'New Request Clicked', 'Documents/Drafts');
    console.log("New Request for document:", selectedDoc);
    const documentUrl = apiEndpoint.pdfBaseUri+selectedDoc?.document_url;
    if (!documentUrl) {
        console.error("Document URL not found in response.");
        return;
    }
    const checkResponse = await fetch(documentUrl, { method: 'HEAD' });
    if (!checkResponse.ok) {
        console.error(`Document not found: ${documentUrl}`);
        return;
    }
    const documentData = { data: selectedDoc, fullView: true };
    localStorage.setItem("uploadedDocument", JSON.stringify(documentData));
    navigate("/esign/pdfeditor", { replace: true }); 
  };

  useEffect(() => {
    if (selectedPreviewDoc) {
      handlePreview(selectedPreviewDoc);
    }
  }, [selectedPreviewDoc]);

  const handlePreview = async (selectedPreviewDoc) => {
    logEvent('Clicked', 'Preview Clicked', 'Documents/Drafts');
    if(pdfEditortoken?.access && selectedPreviewDoc?.document_id)
    {
      setLoader(true)
      console.log(selectedPreviewDoc)
      try {
        const response = await pdfToolApi.getSelectedDocuments(pdfEditortoken?.access, selectedPreviewDoc?.document_id );
        setLoader(false)
        console.log(response)
        if (response.status) {
          const documentUrl = apiEndpoint.pdfBaseUri+response?.data?.document_url;

          if (!documentUrl) {
              console.error("Document URL not found in response.");
              return;
          }
          const checkResponse = await fetch(documentUrl, { method: 'HEAD' });
          if (!checkResponse.ok) {
              console.error(`Document not found: ${documentUrl}`);
              return;
          }
          var previewDocumnetData = {document_url: `${apiEndpoint.pdfBaseUri}/${response?.data?.document_url}`, document_name: response?.data?.document_name}
          setPreviewDocument(true)
          setPreviewDoc(previewDocumnetData)
        }
      } catch (error) {
        setLoader(false)
        if (error.response?.status === 401 && error.response?.data?.code === "token_not_valid") {
          console.warn("Access token expired, attempting to refresh...");
          
          if (!refreshTokenCalled) { // Prevent infinite retry
            refreshTokenCalled = true;
            dispatch(refreshToken())
              .unwrap()
              .then((newTokenData) => {
                if (newTokenData.access) {
                  handlePreview(selectedDoc);
                  refreshTokenCalled = false;
                }
              })
              .catch((refreshError) => {
                console.error("Token refresh failed, user may need to log in again.", refreshError);
              });
          }
        } else {
          console.error("Error fetching documents:", error);
        }
      } 
    }
  };

  let refreshTokenCalled = false;

  const handleDownload = async (doc) => {
    logEvent('Clicked', 'Download Button Clicked', 'Documents/Drafts');
    setLoader(true);
    try {
      const documentUrl = `${apiEndpoint.pdfBaseUri}/${doc?.document_url}`;
      const response = await fetch(documentUrl);
      if (!response.ok) {
        setLoader(false);
        throw new Error("Network response was not ok");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${doc.document_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setLoader(false);
    } catch (error) {
      console.error("Error downloading the PDF:", error);
    }
  };

  const handleDelete = async () => {
    console.log(draftDoc)
    logEvent('Clicked', `Delete Button Clicked${draftDoc?.document_id}`, 'Documents/Drafts');
    if(pdfEditortoken?.access && draftDoc?.document_id)
    {
      try {
        setLoader(true);
        const response = await pdfToolApi.deleteSelectedPdf(pdfEditortoken?.access, draftDoc?.document_id );
        setLoader(false)
        console.log(response)
        if (response.status) {
          fetchAllDocuments(pdfEditortoken?.access);
          setDeleteDraftPdfOpen(false);
          setDraftDoc(null);
        }
      } catch (error) {
        console.error("Error deleting the PDF:", error);
      }
    }
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const filteredDocs = documents.filter(
    (doc) =>
      doc.document_name.toLowerCase().includes(searchText.toLowerCase()) ||
      doc.document_id.toString().includes(searchText)
  );

  return (
    <>
      <Box sx={{ display: "flex", ml: "30px" }}>
        <TextField
          placeholder="Search..."
          type="text"
          variant="outlined"
          onChange={handleSearchChange}
          sx={{
          boxSizing: "border-box",
          borderRadius: "10px",
          backgroundColor: "#fff",
          borderColor: "#E8E8E9",
          borderWidth: "5px",
          "& .MuiOutlinedInput-root": {
              borderColor: "#E8E8E9",
              backgroundColor: "none",
              "&:hover fieldset": {
              borderColor: "#E8E8E9",
              },
              "&.Mui-focused fieldset": {
              borderColor: "#E8E8E9",
              },
          },
          "& .MuiFormHelperText-root": {
              color: "#FF6666",
          },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {esignIcons.searchIcon}
              </InputAdornment>
            ),
            sx: { 
              borderRadius: "10px",
              width: 410,
              height: 40,
              fontSize: "16px",
              fontWeight: "500",
              "& input::-ms-reveal, & input::-ms-clear": {
                  display: "none",
              },
              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                  display: "none",
                  WebkitAppearance: "none",
                  margin: 0,
              },
              "& input[type=number]": {
                  MozAppearance: "textfield",
              },
            },
          }}
        />
      </Box>
      <Paper sx={{ width: "100%", overflow: "auto", boxShadow: "none", height: "90%", mt: "15px" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ background: "#F5F5F5", fontSize: "15px", fontWeight: 500, color: "#1D1D1D" }}>
              <TableRow>
                <TableCell sx={{  pl: "30px", }}>S. No.</TableCell>
                {/* <TableCell>Document ID</TableCell> */}
                <TableCell>Document Name</TableCell>
                <TableCell>Created On</TableCell>
                <TableCell>Updated On</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableloader ? (
                [...Array(rowsPerPage)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ pl: "30px" }}>
                      <Skeleton variant="text" width={30} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={180} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={120} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="circular" width={30} height={30} />
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredDocs.length > 0 ? (
                [...filteredDocs]
                .reverse()
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow key={row.document_id} sx={{
                      "&:hover": { backgroundColor: "#fafafa" },
                    }} onClick={() => setSelectedDoc(row)} >
                      <TableCell sx={{  pl: "30px", cursor: "pointer" }}>{(page * rowsPerPage + index + 1).toString().padStart(2, "0")}</TableCell>
                      {/* <TableCell sx={{ cursor: "pointer" }}>{row.document_id}</TableCell> */}
                      <TableCell sx={{ cursor: "pointer" }}>{row.document_name?.length > 30 ? row.document_name.slice(0, 30) + "..." : row.document_name}</TableCell>
                      <TableCell sx={{ cursor: "pointer" }}>
                        {new Date(row.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ cursor: "pointer" }}>
                        {new Date(row.updated_at).toLocaleString()}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          onClick={() => {
                            setSelectedPreviewDoc(null);
                            setTimeout(() => setSelectedPreviewDoc(row), 0);
                          }}
                        >
                          {esignIcons.eyeIcon}
                        </IconButton>
                        <IconButton
                          onClick={() => handleDownload(row)}
                        >
                          {esignIcons.downloadIcon}
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setDraftDoc(null);
                            setTimeout(() => setDraftDoc(row), 0);
                            setDeleteDraftPdfOpen(true);
                          }}
                        >
                          {esignIcons.deleteIconRed18}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No documents found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10]}
          component="div"
          count={filteredDocs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) =>
            setRowsPerPage(parseInt(event.target.value, 10))
          }
        />
      </Paper>

      <Modal open={deleteDraftPdfOpen} onClose={() => setDeleteDraftPdfOpen(false)}>
          <Box
              sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "434px",
                  height: "368px",
                  backgroundColor: "#fff",
                  boxShadow: 24,
                  overflow: "auto",
                  borderRadius: "20px",
              }}
          >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "1em 1em  1em 2em" }}>
                  <Typography variant="h6" sx={{ fontSize: "22px", fontWeight: 600, color: "#1D1D1D" }}>Delete PDF</Typography>
              </Box>
              <Divider sx={{ borderWidth : "1px", margin: 0, padding: 0 }} />
              <Box sx={{ p: "1em 2em", mb: 3, display: "flex", flexDirection: "column", justifyContent: "space-between", height: "74%" }}>
                  <Box>
                      <img src={`${process.env.PUBLIC_URL}/images/delete.gif`} alt="delete" style={{ height: 83 }} />
                  </Box>
                  <Typography variant="p" sx={{ fontSize: "20px", fontWeight: 400, color: "#1D1D1D" }}>
                    Are you sure you want to delete this PDF?
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: "auto", gap: 2 }}>
                      <Button variant="contained" sx={{ width: '171px', height: 46, color: "#1D1D1D", background: "transparent", textTransform: "none", fontSize: "16px", borderRadius: "10px", border: "1px solid #D2D2D2", boxShadow: "none", fontWeight: "500",}}  onClick={() => setDeleteDraftPdfOpen(false)} >
                          No
                      </Button>
                      <Button variant="contained" sx={{ width: '171px', height: 46, color: "#ffffff", background: "#C5060B", textTransform: "none", fontSize: "16px", borderRadius: "10px", border: "1px solid #C5060B", boxShadow: "none", fontWeight: "500",}} onClick={handleDelete} >
                          Delete
                      </Button>
                  </Box>
              </Box>
          </Box>
      </Modal>

      {loader && <Loader />}
      {previewDocument && <PdfPreview previewDoc={previewDoc} setPreviewDoc={setPreviewDoc} previewDocument={previewDocument} setPreviewDocument={setPreviewDocument} />}
      </>
  );
};

export default DraftDocuments;
